<template>
  <div
    :class="[
        type === 'textarea' ? 'ui-textarea' : 'ui-input',
        {
          'is-inline': inline,
          'is-disabled': disabled,
          'ui-input-group': $slots.prepend || $slots.append,
          'ui-input-group--append': $slots.append,
          'ui-input-group--prepend': $slots.prepend,
        }
      ]"
    :data-type="type"
  >
    <template v-if="type !== 'textarea'">
      <div class="ui-input-group__prepend" v-if="$slots.prepend">
        <slot name="prepend"></slot>
      </div>
      <input
        ref="input"
        class="ui-input__inner uk-input"
        :class="inputClass"
        :style="inputStyle"
        v-bind="$attrs"
        :type="type"
        :id="computedId"
        :disabled="disabled"
        :value="currentValue"
        :maxlength="maxChars ? maxChars : null"
        @input="handleInput"
        @change="handleChange"
        @blur="handleBlur"
      />
      <div
        v-if="mailCheckedEmail && !hideMailCheckedEmail"
        style="font-size: 14px"
      >
        Did you mean <span>{{ mailCheckedEmail }}</span>?
        <button
          type="button"
          class="uk-icon-button uk-button-danger uk-margin-small-right"
          @click="acceptMailCheckedEmail"
        >
          <span uk-icon="icon: check"></span></button>
        <button
          type="button"
          class="uk-icon-button uk-button-default"
          @click="rejectMailCheckedEmail"
        >
          <span uk-icon="icon: close"></span></button>
      </div>

      <div class="ui-input-group__append" v-if="$slots.append">
        <slot name="append"></slot>
      </div>
    </template>

    <textarea
      v-else
      ref="input"
      class="ui-textarea__inner uk-textarea"
      :class="inputClass"
      :style="inputStyle"
      v-bind="$attrs"
      :id="computedId"
      :disabled="disabled"
      :value="currentValue"
      @input="handleInput"
      @change="handleChange"
      @blur="handleBlur"></textarea>

    <div
      v-if="maxChars || maxWords"
      style="font-size: 14px; font-variant-numeric: tabular-nums;"
    >
      <div v-if="maxChars">{{ numChars }} / {{ maxChars }} chars</div>
      <div v-if="maxWords">{{ numWords }} / {{ maxWords }} words</div>
    </div>
  </div>
</template>

<script>
import InputMask from 'inputmask'
import emitter from '../../../common/mixin/emitter'
import Mailcheck from 'mailcheck'

Mailcheck.defaultDomains.push('zionchurch.org')

export default {
  name: 'UiFormInput',

  mixins: [emitter],

  inheritAttrs: false,

  inject: {
    uiFormItem: {
      default: '',
    },
  },

  props: {
    type: {
      type: String,
      default: 'text',
    },

    id: String,
    disabled: Boolean,
    value: [String, Number],
    inline: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: [Array, Object, String],
      default() {
        return []
      },
    },
    inputStyle: {
      type: [Array, Object],
      default() {
        return {}
      },
    },
    maxChars: {
      type: Number,
    },
    maxWords: {
      type: Number,
    },
    mask: {
      type: String,
    },
    maskOpts: {
      type: Object,
      default() {
        return {}
      },
    },
  },

  data() {
    return {
      currentValue: this.value === undefined || this.value === null ? '' : this.value,
      mailCheckedEmail: null,
      hideMailCheckedEmail: false,
    }
  },

  computed: {
    computedId() {
      if (this.id) {
        return this.id
      }

      return this.uiFormItem.computedId
    },

    numChars() {
      if (this.type !== 'file' && this.currentValue) {
        return String(this.currentValue).length
      }
      return 0
    },

    numWords() {
      if (this.type !== 'file' && this.currentValue) {
        return String(this.currentValue).trim().split(/\s+/).length
      }
      return 0
    },
  },

  watch: {
    /**
     * Responsible for updating internal value when outside value changes.
     */
    value() {
      this.refreshCurrentValue()
    },
  },

  methods: {
    refreshCurrentValue() {
      this.currentValue = this.value === undefined || this.value === null ? '' : this.value
    },

    getTruncatedValue(value) {
      if (this.maxWords) {
        const words = value.split(/\s+/).filter(word => word.length > 0)
        const wordCount = words.length
        if (wordCount > this.maxWords) {
          value = words.slice(0, this.maxWords).join(' ')
        }
      }
      return value
    },

    handleInput(ev) {
      if (this.type === 'file') {
        this.$emit('input', ev.target.files)
      } else {
        let { value } = ev.target
        value = this.getTruncatedValue(value)
        this.changeValue(value)
      }
    },

    handleChange(ev) {
      if (this.type === 'file') {
        this.$emit('change', ev.target.files)
      } else {
        this.$emit('change', ev.target.value)
      }
    },

    handleBlur(ev) {
      this.verifyEmail()
      // Force input to reflect potentially truncated value from handleInput:
      if (this.type !== 'file' && ev.target.value !== this.currentValue) {
        ev.target.value = this.currentValue
      }
      this.$emit('blur', ev)
      this.dispatch('UiFormItem', 'ui.form.blur', [this.currentValue])
    },

    changeValue(value) {
      this.setCurrentValue(value)
      this.$emit('input', value)
    },

    setCurrentValue(value) {
      this.currentValue = value
    },

    verifyEmail() {
      if (this.type !== 'email') {
        return
      }

      this.hideMailCheckedEmail = false

      Mailcheck.run({
        email: this.currentValue,
        suggested: (suggestion) => {
          this.mailCheckedEmail = suggestion.full
        },
        empty() {
          // nothing wrong with the email
        },
      })
    },

    acceptMailCheckedEmail() {
      this.currentValue = this.mailCheckedEmail
      this.$emit('input', this.mailCheckedEmail)
      this.mailCheckedEmail = null
    },

    rejectMailCheckedEmail() {
      this.mailCheckedEmail = null
    },
  },

  created() {
    this.refreshCurrentValue()
  },

  mounted() {
    this.$nextTick(() => {
      if (this.mask) {
        const im = new InputMask(this.mask, this.maskOpts)
        im.mask(this.$refs.input)
      }
    })
  },
}
</script>
